"use client";

import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { getStockListData } from "../../../services/TradingMallService";
import type { IStockListRequest, IFilters, IStockItem } from "../../../interfaces/StockList.interface";
import {
  createDefaultStockFilters,
  serializeStockFilters,
} from "../utils/stockListingHelpers";

interface UseStockListingProps {
  initialStocks?: IStockItem[];
  entity: string;
  exchId?: number;
  sort?: string;
  sortField?: string;
  filters?: IFilters;
  limit?: number;
  initialShowMore?: boolean;
}

interface UseStockListingReturn {
  stocks: IStockItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasError: boolean;
  page: number;
  showMore: boolean;
  showLess: boolean;
  handleShowMore: () => void;
  handleShowLess: () => void;
  refetch: () => void;
}

export const useStockListing = ({
  initialStocks = [],
  entity,
  exchId = 0,
  sort = "amount_high_to_low",
  sortField,
  filters,
  limit = 20,
  initialShowMore = false,
}: UseStockListingProps): UseStockListingReturn => {
  const [stocks, setStocks] = useState<IStockItem[]>(initialStocks);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [page, setPage] = useState(1);
  const [showMore, setShowMore] = useState(initialShowMore);
  const [showLess, setShowLess] = useState(false);
  const hasRestoredRef = useRef(false);
  const prevFiltersKeyRef = useRef<string>(
    serializeStockFilters(filters ?? createDefaultStockFilters()),
  );

  const defaultFilters = useMemo<IFilters>(
    () => createDefaultStockFilters(),
    [],
  );

  const applyResponse = (
    response: { stockList?: IStockItem[]; showMore?: boolean } | undefined,
    pageNum: number,
    isLoadMore: boolean,
  ) => {
    const stockList = response?.stockList;

    if (stockList && stockList.length > 0) {
      if (isLoadMore) {
        setStocks((prevStocks) => [...prevStocks, ...stockList]);
      } else {
        setStocks(stockList);
      }

      const nextShowMore = response?.showMore ?? false;
      setShowMore(nextShowMore);
      setShowLess(nextShowMore && pageNum > 1);
      return;
    }

    // No data in response
    if (!isLoadMore) {
      setStocks([]);
    }
    setShowMore(false);
    setShowLess(false);
  };

  const fetchStocks = useCallback(
    async (pageNum: number, isLoadMore: boolean = false) => {
      setIsLoading(true);
      if (isLoadMore) setIsLoadingMore(true);
      setHasError(false);

      try {
        const requestBody: IStockListRequest = {
          entity,
          exchId,
          page: pageNum,
          limit,
          sort,
          ...(sortField ? { sortField } : {}),
          filters: filters || defaultFilters,
        };

        const response = await getStockListData(requestBody);

        if (response?.stockList || response?.data?.stockList) {
          applyResponse(
            {
              stockList: response.stockList ?? response.data?.stockList,
              showMore: response.showMore ?? response.data?.showMore,
            },
            pageNum,
            isLoadMore,
          );
        } else {
          applyResponse(undefined, pageNum, isLoadMore);
        }
      } catch {
        setHasError(true);
        if (!isLoadMore) {
          setStocks([]);
        }
        setShowMore(false);
        setShowLess(false);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [entity, exchId, sort, sortField, filters, limit, defaultFilters],
  );

  // Track previous initial stocks to detect changes
  const prevInitialStocksRef = useRef<string>('');
  
  // Initialize with initial stocks on mount or when they change (e.g., sort change)
  useEffect(() => {
    const currentInitialStocksKey = initialStocks.map(s => s.symbol).join(',');
    
    // Reset when initial stocks change (e.g., when sort changes)
    if (hasRestoredRef.current && prevInitialStocksRef.current !== currentInitialStocksKey) {
      setStocks(initialStocks);
      setShowMore(initialShowMore);
      setShowLess(false);
      setPage(1);
      prevInitialStocksRef.current = currentInitialStocksKey;
      return;
    }
    
    // Initial mount: mark restored even for empty SSR datasets so
    // subsequent filter changes can trigger client refetches.
    if (!hasRestoredRef.current) {
      setStocks(initialStocks);
      setShowMore(initialShowMore);
      setShowLess(false);
      prevInitialStocksRef.current = currentInitialStocksKey;
      hasRestoredRef.current = true;
    }
  }, [initialStocks, initialShowMore]);

  // Fetch more stocks when page changes (but not on initial render)
  useEffect(() => {
    if (page > 1 && hasRestoredRef.current) {
      fetchStocks(page, true);
    }
  }, [page, fetchStocks]);

  // Refetch when filters change (server applies filters on get-stockList)
  useEffect(() => {
    if (!hasRestoredRef.current) return;
    const nextKey = serializeStockFilters(filters ?? defaultFilters);
    if (prevFiltersKeyRef.current !== nextKey) {
      setPage(1);
      fetchStocks(1, false);
    }
    prevFiltersKeyRef.current = nextKey;
  }, [filters, fetchStocks, defaultFilters]);

  const handleShowMore = useCallback(() => {
    setPage((prevPage) => prevPage + 1);
  }, []);

  const handleShowLess = useCallback(() => {
    // Reset to page 1 and use initial stocks
    setPage(1);
    setStocks(initialStocks);
    setShowMore(initialShowMore);
    setShowLess(false);
  }, [initialStocks, initialShowMore]);

  const refetch = useCallback(() => {
    setPage(1);
    setStocks(initialStocks);
    setShowMore(initialShowMore);
    setShowLess(false);
    setHasError(false);
    hasRestoredRef.current = true;
  }, [initialStocks, initialShowMore]);

  return {
    stocks,
    isLoading,
    isLoadingMore,
    hasError,
    page,
    showMore,
    showLess,
    handleShowMore,
    handleShowLess,
    refetch,
  };
};

export default useStockListing;
